import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { Plus, Edit2, Trash2, MapPin, Building2, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PropertiesPage = () => {
  const { t, language } = useLanguage();
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [isBuildingModalOpen, setIsBuildingModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [propertyFormData, setPropertyFormData] = useState({
    name: '',
    address: '',
    description: ''
  });
  const [buildingFormData, setBuildingFormData] = useState({
    name: '',
    property_id: '',
    floors: '',
    description: '',
    monthly_dues: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propertiesRes, buildingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/properties`, { withCredentials: true }),
        axios.get(`${API_URL}/api/buildings`, { withCredentials: true })
      ]);
      setProperties(propertiesRes.data);
      setBuildings(buildingsRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProperty) {
        await axios.put(`${API_URL}/api/properties/${editingProperty.id}`, propertyFormData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/properties`, propertyFormData, { withCredentials: true });
      }
      toast.success(t('saveSuccess'));
      setIsPropertyModalOpen(false);
      resetPropertyForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleBuildingSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...buildingFormData,
        floors: buildingFormData.floors ? parseInt(buildingFormData.floors) : null,
        monthly_dues: buildingFormData.monthly_dues ? parseFloat(buildingFormData.monthly_dues) : null
      };
      if (editingBuilding) {
        await axios.put(`${API_URL}/api/buildings/${editingBuilding.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/buildings`, payload, { withCredentials: true });
      }
      toast.success(t('saveSuccess'));
      setIsBuildingModalOpen(false);
      resetBuildingForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteProperty = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/properties/${id}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDeleteBuilding = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/buildings/${id}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetPropertyForm = () => {
    setEditingProperty(null);
    setPropertyFormData({ name: '', address: '', description: '' });
  };

  const resetBuildingForm = () => {
    setEditingBuilding(null);
    setBuildingFormData({ name: '', property_id: '', floors: '', description: '', monthly_dues: '' });
  };

  const editProperty = (property) => {
    setEditingProperty(property);
    setPropertyFormData({
      name: property.name || '',
      address: property.address || '',
      description: property.description || ''
    });
    setIsPropertyModalOpen(true);
  };

  const editBuilding = (building) => {
    setEditingBuilding(building);
    setBuildingFormData({
      name: building.name || '',
      property_id: building.property_id || '',
      floors: building.floors?.toString() || '',
      description: building.description || '',
      monthly_dues: building.monthly_dues?.toString() || ''
    });
    setIsBuildingModalOpen(true);
  };

  const getBuildingsForProperty = (propertyId) => {
    return buildings.filter(b => b.property_id === propertyId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('properties')}</h1>
          <p className="text-gray-500 text-sm mt-1">{properties.length} {t('properties').toLowerCase()}, {buildings.length} {t('buildings').toLowerCase()}</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => { resetBuildingForm(); setIsBuildingModalOpen(true); }}
            variant="outline"
            className="rounded-none"
            data-testid="add-building-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('addBuilding')}
          </Button>
          <Button 
            onClick={() => { resetPropertyForm(); setIsPropertyModalOpen(true); }}
            className="bg-black text-white rounded-none hover:bg-gray-800"
            data-testid="add-property-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('addProperty')}
          </Button>
        </div>
      </div>

      {/* Properties Grid */}
      <div className="space-y-6">
        {properties.map((property) => (
          <div key={property.id} className="bg-white border border-black" data-testid={`property-${property.id}`}>
            {/* Property Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-black">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold">{property.name}</h3>
                  <p className="text-sm text-gray-500">{property.address}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => editProperty(property)} 
                  className="p-2 hover:bg-gray-200"
                  data-testid={`edit-property-${property.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDeleteProperty(property.id)} 
                  className="p-2 hover:bg-red-100 text-red-600"
                  data-testid={`delete-property-${property.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Buildings */}
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getBuildingsForProperty(property.id).map((building) => (
                  <div 
                    key={building.id} 
                    className="border border-gray-200 p-4 hover:border-black transition-colors"
                    data-testid={`building-${building.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="font-semibold">{building.name}</span>
                      </div>
                      <div className="flex gap-1">
                        <button 
                          onClick={() => editBuilding(building)} 
                          className="p-1 hover:bg-gray-100"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button 
                          onClick={() => handleDeleteBuilding(building.id)} 
                          className="p-1 hover:bg-red-100 text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 space-y-1">
                      <div>{building.floors ? `${building.floors} ${t('floors').toLowerCase()}` : t('floors') + ' -'}</div>
                      {building.monthly_dues > 0 && (
                        <div className="font-mono font-medium text-black">
                          {t('monthlyDues')}: {language === 'tr' ? '₺' : '$'}{building.monthly_dues.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {getBuildingsForProperty(property.id).length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-400 border border-dashed">
                    {t('noBuildings')}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {properties.length === 0 && (
          <div className="text-center py-12 text-gray-500 border border-dashed">
            {t('noData')}
          </div>
        )}
      </div>

      {/* Property Modal */}
      <Dialog open={isPropertyModalOpen} onOpenChange={setIsPropertyModalOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingProperty ? t('edit') + ' ' + t('properties') : t('addProperty')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePropertySubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('properties')} {language === 'tr' ? 'Adı' : 'Name'} *</label>
              <Input
                value={propertyFormData.name}
                onChange={(e) => setPropertyFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                className="rounded-none mt-1"
                data-testid="property-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('address')} *</label>
              <Input
                value={propertyFormData.address}
                onChange={(e) => setPropertyFormData(prev => ({ ...prev, address: e.target.value }))}
                required
                className="rounded-none mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('description')}</label>
              <textarea
                value={propertyFormData.description}
                onChange={(e) => setPropertyFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                rows="3"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsPropertyModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="property-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Building Modal */}
      <Dialog open={isBuildingModalOpen} onOpenChange={setIsBuildingModalOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingBuilding ? t('edit') + ' ' + t('buildings') : t('addBuilding')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBuildingSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('buildings')} {language === 'tr' ? 'Adı' : 'Name'} *</label>
              <Input
                value={buildingFormData.name}
                onChange={(e) => setBuildingFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                className="rounded-none mt-1"
                data-testid="building-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">{t('properties')} *</label>
              <select
                value={buildingFormData.property_id}
                onChange={(e) => setBuildingFormData(prev => ({ ...prev, property_id: e.target.value }))}
                required
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                disabled={!!editingBuilding}
              >
                <option value="">{t('selectProperty')}</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('floors')}</label>
                <Input
                  type="number"
                  value={buildingFormData.floors}
                  onChange={(e) => setBuildingFormData(prev => ({ ...prev, floors: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('monthlyDues')}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={buildingFormData.monthly_dues}
                  onChange={(e) => setBuildingFormData(prev => ({ ...prev, monthly_dues: e.target.value }))}
                  placeholder="0"
                  className="rounded-none mt-1"
                  data-testid="building-dues-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t('description')}</label>
              <textarea
                value={buildingFormData.description}
                onChange={(e) => setBuildingFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                rows="3"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsBuildingModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="building-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertiesPage;
